import type { Week } from 'chery-contracts';
import { useState, type MouseEvent } from 'react';
import { BLANK_DISPLAY } from 'chery-kpi-engine';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { chartScale, compactTick, isolatedPoints, labelEvery, lastPointIndex, linePath, SERIES_SLOTS, weekShortLabel } from './reports-display';

export interface TrendSeries {
  kpiId: string;
  name: string;
  /** One point per week; `null` is a gap. */
  points: readonly (number | null)[];
}

export interface TrendChartProps {
  /** `${unit}|${currency}` of the group — every series shares that unit and currency. */
  groupKey: string;
  title: string;
  weeks: readonly Pick<Week, 'id' | 'weekDate'>[];
  series: readonly TrendSeries[];
  /** Drawn only for one KPI whose target is compared with a weekly-sized figure. */
  target: { value: number; label: string } | null;
  formatPoint: (value: number) => string;
}

const WIDTH = 720;
const HEIGHT = 240;
const PAD = { left: 48, right: 16, top: 14, bottom: 28 };
const PLOT_WIDTH = WIDTH - PAD.left - PAD.right;
const PLOT_HEIGHT = HEIGHT - PAD.top - PAD.bottom;

const seriesColor = (slot: number): string => `var(--series-${slot + 1})`;

/**
 * One weekly trend chart in inline SVG (plan §2.1 — no chart library). Every series in it shares one unit and one
 * currency (the server groups them), so there is a single honest value axis. Blank weeks are gaps; a lone value is a
 * dot. Series take the fixed categorical slots in order; past eight they fold into a note pointing at the table.
 * Hovering shows a crosshair and every series' value that week.
 */
export const TrendChart = ({ groupKey, title, weeks, series, target, formatPoint }: TrendChartProps) => {
  const { t } = useI18nTranslation();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const shown = series.slice(0, SERIES_SLOTS);
  const folded = series.length - shown.length;
  const scale = chartScale(
    shown.flatMap((each) => each.points),
    target ? [target.value] : [],
  );
  const count = weeks.length;
  const x = (index: number): number => (count <= 1 ? PAD.left + PLOT_WIDTH / 2 : PAD.left + (index / (count - 1)) * PLOT_WIDTH);
  const y = (value: number): number => PAD.top + PLOT_HEIGHT - ((value - scale.min) / (scale.max - scale.min)) * PLOT_HEIGHT;
  const every = labelEvery(count, PLOT_WIDTH);

  const onMove = (event: MouseEvent<SVGRectElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - box.left) / box.width;

    setHoverIndex(count <= 1 ? 0 : Math.max(0, Math.min(count - 1, Math.round(fraction * (count - 1)))));
  };

  const hoverWeek = hoverIndex === null ? undefined : weeks[hoverIndex];

  return (
    <figure
      data-trend-chart={groupKey}
      className="print-avoid-break rounded-lg border border-border bg-card p-4"
    >
      <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
        {shown.length === 1 ? <span className="text-xs text-foreground">{shown[0]?.name}</span> : null}
      </figcaption>

      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="block h-auto w-full"
          role="img"
          aria-label={t('reports.chart.aria', { title })}
        >
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="hsl(var(--border))"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 6}
                y={y(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={10}
                fill="hsl(var(--muted-foreground))"
              >
                {compactTick(tick)}
              </text>
            </g>
          ))}

          {weeks.map((week, index) =>
            index % every === 0 ? (
              <text
                key={week.id}
                x={x(index)}
                y={HEIGHT - PAD.bottom + 16}
                textAnchor="middle"
                fontSize={10}
                fill="hsl(var(--muted-foreground))"
              >
                {weekShortLabel(week)}
              </text>
            ) : null,
          )}

          {target ? (
            <g data-target-line>
              <line
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(target.value)}
                y2={y(target.value)}
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <text
                x={WIDTH - PAD.right}
                y={y(target.value) - 5}
                textAnchor="end"
                fontSize={10}
                fill="hsl(var(--foreground))"
              >
                {target.label}
              </text>
            </g>
          ) : null}

          {shown.map((each, slot) => (
            <g
              key={each.kpiId}
              data-series={each.kpiId}
            >
              <path
                d={linePath(each.points, x, y)}
                fill="none"
                stroke={seriesColor(slot)}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {[...new Set([...isolatedPoints(each.points), lastPointIndex(each.points), ...(hoverIndex === null ? [] : [hoverIndex])])]
                .filter((index) => index >= 0 && each.points[index] !== null && each.points[index] !== undefined)
                .map((index) => (
                  <circle
                    key={index}
                    cx={x(index)}
                    cy={y(each.points[index] as number)}
                    r={4}
                    fill={seriesColor(slot)}
                    stroke="hsl(var(--card))"
                    strokeWidth={2}
                  />
                ))}
            </g>
          ))}

          {hoverIndex !== null ? (
            <line
              x1={x(hoverIndex)}
              x2={x(hoverIndex)}
              y1={PAD.top}
              y2={HEIGHT - PAD.bottom}
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1}
            />
          ) : null}

          <rect
            x={PAD.left}
            y={PAD.top}
            width={PLOT_WIDTH}
            height={PLOT_HEIGHT}
            fill="transparent"
            onMouseMove={onMove}
            onMouseLeave={() => setHoverIndex(null)}
          />
        </svg>

        {hoverWeek && hoverIndex !== null ? (
          <div
            className="no-print pointer-events-none absolute top-1 z-10 min-w-[10rem] rounded-md border border-border bg-card px-2.5 py-2 text-xs shadow-lg"
            style={{ left: `${Math.min(Math.max((x(hoverIndex) / WIDTH) * 100, 10), 70)}%` }}
          >
            <p className="mb-1 font-semibold text-foreground">{weekShortLabel(hoverWeek)}</p>
            {shown.map((each, slot) => {
              const value = each.points[hoverIndex] ?? null;

              return (
                <p
                  key={each.kpiId}
                  className="flex items-center gap-1.5"
                >
                  <span
                    aria-hidden
                    className="inline-block h-0.5 w-3 rounded"
                    style={{ background: seriesColor(slot) }}
                  />
                  <span className="flex-1 text-muted-foreground">{each.name}</span>
                  <span className="font-mono font-semibold text-foreground">{value === null ? BLANK_DISPLAY : formatPoint(value)}</span>
                </p>
              );
            })}
          </div>
        ) : null}
      </div>

      {shown.length >= 2 || folded > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {shown.map((each, slot) => (
            <li
              key={each.kpiId}
              className="flex items-center gap-1.5"
            >
              <span
                aria-hidden
                className="inline-block h-0.5 w-3 rounded"
                style={{ background: seriesColor(slot) }}
              />
              {each.name}
            </li>
          ))}
          {folded > 0 ? <li>{t('reports.chart.more', { count: folded })}</li> : null}
        </ul>
      ) : null}
    </figure>
  );
};
